import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaStar, FaRegStar, FaStarHalfAlt, FaThumbsUp } from 'react-icons/fa';
import { RiEmotionHappyLine } from 'react-icons/ri';
import { FiAward, FiHome, FiShield, FiDollarSign, FiMapPin } from 'react-icons/fi';
import { LiaRupeeSignSolid } from 'react-icons/lia';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const ReviewsPage = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        // Fetch all reviews
        const reviewsResponse = await fetch(`${BQ_BASE_URL}/bq/api/feedback/`);
        const reviewsData = await reviewsResponse.json();
        setReviews(reviewsData);

        // Fetch review statistics
        const statsResponse = await fetch(`${BQ_BASE_URL}/bq/api/feedback/stats/ratings`);
        const statsData = await statsResponse.json();
        setReviewStats(statsData);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaRegStar key={i} className="text-yellow-400" />);
      }
    }
    return stars;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getCompetitorComparisonText = (value) => {
    switch (value) {
      case 'much_better':
        return 'Much better than competitors';
      case 'better':
        return 'Better than competitors';
      case 'same':
        return 'About the same as competitors';
      case 'worse':
        return 'Worse than competitors';
      case 'much_worse':
        return 'Much worse than competitors';
      default:
        return 'Compared to competitors';
    }
  };

  const filteredReviews =
    activeFilter === 'all'
      ? reviews
      : reviews.filter((review) => review.message && review.message.trim() !== '');

  // Safe access to reviewStats with fallbacks
  // Update the safeStats object to match your API response
  const safeStats = {
    recommendation_rate: reviewStats?.recommendation_rate?.replace('%', '') || '0', // Remove % sign
    total_feedbacks: reviewStats?.total_feedbacks || 0,
    average_ratings: {
      overall: reviewStats?.average_ratings?.overall || 0,
      cleanliness: reviewStats?.average_ratings?.cleanliness || 0,
      value_for_money: reviewStats?.average_ratings?.value_for_money || 0,
      // These might not be in your response, so we'll provide defaults
      service: reviewStats?.average_ratings?.service || 0,
      location: reviewStats?.average_ratings?.location || 0,
    },
  };

  // Update the rating categories to match your API keys
  const ratingCategories = [
    { name: 'Overall', icon: <FiAward className="mr-2" />, key: 'overall' },
    {
      name: 'Cleanliness',
      icon: <FiHome className="mr-2" />,
      key: 'cleanliness',
    },
    { name: 'Service', icon: <FiShield className="mr-2" />, key: 'service' },
    {
      name: 'Value For Money',
      icon: <LiaRupeeSignSolid size={20} className="mr-2" />,
      key: 'value_for_money',
    },
    { name: 'Location', icon: <FiMapPin className="mr-2" />, key: 'location' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 relative mt-16">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-8 left-8 text-black hover:text-gray-700 transition font-semibold flex items-center"
      >
        <IoMdArrowRoundBack size={32} className="mr-1" />
      </button>

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Guest Reviews</h1>
          {!loading && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center bg-gray-100 px-4 py-2 rounded-full">
                <RiEmotionHappyLine className="text-black mr-2 text-xl" />
                <span className="text-black font-medium">
                  {safeStats.recommendation_rate}% recommend
                </span>
              </div>
              <div className="flex items-center bg-gray-100 px-4 py-2 rounded-full">
                <FaStar className="text-black mr-2" />
                <span className="text-black font-medium">
                  {safeStats.average_ratings.overall.toFixed(1)} average rating
                </span>
              </div>
              <div className="flex items-center bg-gray-100 px-4 py-2 rounded-full">
                <span className="text-black font-medium">
                  Total {safeStats.total_feedbacks} reviews
                </span>
              </div>
            </div>
          )}
        </div>

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {ratingCategories.map((category) => (
              <div
                key={category.key}
                className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
              >
                <div className="flex items-center text-gray-700 mb-2">
                  {category.icon}
                  <span className="font-medium">{category.name}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-2xl font-bold mr-2">
                    {safeStats.average_ratings[category.key]?.toFixed(1) || '0.0'}
                  </span>
                  <div className="flex">
                    {renderStars(safeStats.average_ratings[category.key] || 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Guest Feedback</h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeFilter === 'all'
                  ? 'bg-black text-gray-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Reviews
            </button>
            <button
              onClick={() => setActiveFilter('with_comments')}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeFilter === 'with_comments'
                  ? 'bg-black text-gray-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              With Comments
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading reviews...</p>
          </div>
        ) : filteredReviews.length > 0 ? (
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="space-y-6">
              {filteredReviews.map((review, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800 capitalize">{review.name}</h4>
                      <div className="flex items-center mt-1">
                        <div className="flex mr-2">{renderStars(review.overall_rating)}</div>
                        <span className="text-gray-500 text-sm">
                          {formatDate(review.submission_date)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {review.would_recommend && (
                        <div className="flex items-center bg-green-50 px-2 py-1 rounded text-sm">
                          <FaThumbsUp className="text-black mr-1" />
                          <span className="text-black">Recommends</span>
                        </div>
                      )}
                      <div className="text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded">
                        {getCompetitorComparisonText(review.compared_to_competitors)}
                      </div>
                    </div>
                  </div>
                  {review.message && <p className="text-gray-600 mt-3">{review.message}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {review.cleanliness_rating === 5 && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                        Clean
                      </span>
                    )}
                    {review.service_rating === 5 && (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                        Great Service
                      </span>
                    )}
                    {review.value_for_money_rating === 5 && (
                      <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                        Good Value
                      </span>
                    )}
                    {review.location_rating === 5 && (
                      <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                        Great Location
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No reviews match your filter criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;
